import { useMemo, useState, useEffect, ReactElement } from 'react'
import {
    LuxInputSwitch,
    LuxIconSpinner,
    LuxCheckboxInput,
    LuxAutocomplete,
    Color,
} from '@lux/wardrobe-lib'
import OutlinedButton from '../../UI/OutlinedButton/OutlinedButton'
import './FilterSubCategoryItem.scss'
import { useIntl } from 'react-intl'
import { statusColors } from '@/app/utils/statusColorMap'
import { FilterSubcategoryItemProps, ItemList } from '@/app/models/filters'

const FilterSubCategoryItem = (props: FilterSubcategoryItemProps) => {
    const {
        label,
        filterValue = [],
        setFilterValue,
        setListOptions,
        listOptions,
        showBadge,
        customBadge,
        onReset,
        type,
        customContent,
        optionLabel,
        optionValue,
        disabled,
        minDigit,
        isLoading,
        isCollapsed,
        keepOpen,
    } = props

    const intl = useIntl()

    const [collapsed, setCollapsed] = useState(
        props.isCollapsed !== undefined ? props.isCollapsed : true
    )

    const onPressHeader = () => {
        if (!isSwitch && !keepOpen) {
            setCollapsed(!collapsed)
        }
    }

    const isSwitch = type === 'SWITCH'

    let content = <></>

    switch (type) {
        case 'STATUS':
            content = (
                <FilterSubCategoriesStatusOptions
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    subCategoryName={label}
                    onReset={() => {
                        return
                    }}
                />
            )
            break
        case 'MULTISELECT':
            content = (
                <FilterSubCategoriesOptions
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    options={listOptions}
                    translateLabel={props.translateLabel}
                    subCategoryName={label}
                    onReset={onReset}
                    disabled={disabled}
                />
            )
            break
        case 'AUTOCOMPLETE_MULTISELECT':
            content = (
                <LuxAutocomplete
                    multiple
                    disabled={disabled}
                    value={filterValue}
                    predictions={listOptions}
                    onSearchPredictions={setListOptions}
                    onPredictionSelected={() => {}}
                    onChangeCallback={setFilterValue}
                    onClear={() => setFilterValue([])}
                    additionalProps={{ appendTo: 'self' }}
                    optionLabel={optionLabel}
                    optionValue={optionValue}
                    minDigit={minDigit}
                    isLoading={isLoading}
                />
            )
            break
        case 'CUSTOM':
            content = customContent as ReactElement
            break
        case 'SWITCH':
        default:
            break
    }

    const badge = customBadge ? (
        customBadge
    ) : (
        <div className="smart-profile-badge">
            <i className="cu cu-status-ok"></i>
        </div>
    )

    return (
        <div
            className={`smart-modals-filter-subcategories-item ${
                !isSwitch && !collapsed ? 'selected' : ''
            }`}>
            <div className={`header ${!keepOpen ? 'cursor-pointer' : ''}`} onClick={onPressHeader}>
                <h4>{label}</h4>
                <div className="d-flex">
                    {showBadge && !isSwitch && badge}
                    {isSwitch ? (
                        <LuxInputSwitch
                            label={intl.formatMessage({
                                id: `modals.filters.label.show_only_${label}`,
                                defaultMessage: `Show only ${label}`,
                            })}
                            value={filterValue}
                            onChangeCallback={(value: boolean) => {
                                setFilterValue(value)
                            }}
                        />
                    ) : (
                        <i className={collapsed ? 'cu cu-caret-down' : 'cu cu-caret-up'}></i>
                    )}
                </div>
            </div>
            {!collapsed && !isSwitch && <div className="content">{content}</div>}
        </div>
    )
}

const FilterSubCategoriesOptions = (props: {
    filterValue: any
    setFilterValue: (val: any) => void
    options: ItemList[]
    translateLabel: boolean
    subCategoryName: string
    onReset?: () => void
    disabled?: boolean
}) => {
    const {
        filterValue = [],
        setFilterValue,
        options = [],
        subCategoryName,
        onReset,
        disabled,
    } = props

    const [allCheckboxesAreSelected, setAllCheckboxesAreSelected] = useState(false)

    const allIds = useMemo(() => {
        const ids = []
        options.forEach((option) => {
            ids.push(option.value)
        })
        return ids
    }, [])

    useEffect(() => {
        setAllCheckboxesAreSelected(filterValue?.length === allIds?.length)
    }, [filterValue, allIds])

    const onClickAllCheckboxes = () => {
        if (allCheckboxesAreSelected) {
            setFilterValue([])
        } else {
            setFilterValue(allIds)
        }
    }

    const onClickCheckbox = (idInput: string) => {
        const oldIds = [...filterValue]
        if (oldIds.includes(idInput)) {
            setFilterValue(oldIds.filter((id) => id !== idInput))
        } else {
            setFilterValue([...oldIds, idInput])
        }
    }

    const intl = useIntl()

    return (
        <div className="smart-modals-options-container">
            {options?.length > 0 ? (
                <>
                    <div className="flex justify-content-between align-items-center">
                        <div className="select-all">
                            <LuxCheckboxInput
                                label={intl.formatMessage({
                                    id: 'modals.filters.label.all',
                                    defaultMessage: 'All',
                                })}
                                value={allCheckboxesAreSelected}
                                onChangeCallback={onClickAllCheckboxes}
                                id={`${subCategoryName}-all`}
                                disabled={disabled}
                            />
                        </div>
                        {!disabled && (
                            <div>
                                <OutlinedButton
                                    label={'Reset'}
                                    onClick={() => {
                                        onReset ? onReset() : setFilterValue([])
                                    }}
                                    iconClassName={'cu-reset'}
                                />
                            </div>
                        )}
                    </div>

                    <div className="inputs-container">
                        {options.map((option) => (
                            <div
                                className="flex align-items-center option-item-container "
                                key={option.value}>
                                <LuxCheckboxInput
                                    id={option.value}
                                    label={
                                        props.translateLabel
                                            ? intl.formatMessage({
                                                  id: option.label,
                                                  defaultMessage: option.label,
                                              })
                                            : option.label
                                    }
                                    value={filterValue.includes(option.value)}
                                    onChangeCallback={() => onClickCheckbox(option.value)}
                                    disabled={disabled}
                                />
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="spinner-wrapper flex">
                    <LuxIconSpinner color={Color.GREY2} />
                </div>
            )}
        </div>
    )
}

export const FilterSubCategoriesStatusOptions = ({
    groupOptions = {},
    filterValue = { activity: [] },
    setFilterValue,
    translateLabel = false,
    subCategoryName,
    onReset,
}) => {
    const intl = useIntl()
    const [allCheckboxesAreSelected, setAllCheckboxesAreSelected] = useState(false)

    const groups = Object.keys(groupOptions)

    const items = groups.map((group) => ({
        label: group,
        group,
        options: (groupOptions[group] ?? []).map((option) => ({
            id: `${group}-${option.value}`,
            group: group,
            value: option.value,
            label: option.label,
            dotColor: option.dotColor,
        })),
    }))

    const filterValuesAllSelected = useMemo(() => {
        const newFilterValue = { activity: [] }
        items.forEach((item) => {
            newFilterValue[item.group] = item.options.map((option) => option.value)
        })
        return newFilterValue
    }, [items])

    useEffect(() => {
        setAllCheckboxesAreSelected(
            filterValue.activity?.length === filterValuesAllSelected.activity?.length
        )
    }, [filterValue, filterValuesAllSelected])

    const onClickAllCheckboxes = () => {
        if (allCheckboxesAreSelected) {
            const emptyValues = {}
            groups.forEach((group) => {
                emptyValues[group] = []
            })
            setFilterValue(emptyValues)
        } else {
            setFilterValue(filterValuesAllSelected)
        }
    }

    const onClickReset = () => {
        onReset()
    }

    const onClickCheckbox = (option) => {
        const { group, value } = option

        const oldValues = [...(filterValue[group] ?? [])]

        if (oldValues.includes(value)) {
            const newValues = oldValues.filter((id) => id !== value)
            setFilterValue({ ...filterValue, [group]: newValues })
        } else {
            const newValues = [...oldValues, value]
            setFilterValue({ ...filterValue, [group]: newValues })
        }
    }

    const showSpinner = items.some((item) => item.options?.length === 0)
    return (
        <div className="smart-modals-filter-switch-container">
            {showSpinner ? (
                <div className="spinner-wrapper flex">
                    <LuxIconSpinner color={Color.GREY2} />
                </div>
            ) : (
                <>
                    <div className="flex justify-content-between align-items-center">
                        <div className="select-all">
                            <LuxCheckboxInput
                                label={intl.formatMessage({
                                    id: 'modals.filters.label.all',
                                    defaultMessage: 'All',
                                })}
                                value={allCheckboxesAreSelected}
                                onChangeCallback={onClickAllCheckboxes}
                                id={`${subCategoryName}-all`}
                            />
                        </div>
                        <div>
                            <OutlinedButton
                                label={'Reset'}
                                onClick={onClickReset}
                                iconClassName={'cu-reset'}
                            />
                        </div>
                    </div>
                    <div className="options-container">
                        {items.map((groupOption) => (
                            <div key={groupOption.label}>
                                {groupOption.options?.length > 0 && (
                                    <>
                                        <h4>
                                            {intl.formatMessage({
                                                id: `modals.filters.options_container.${groupOption.label}`,
                                                defaultMessage: groupOption.label,
                                            })}
                                        </h4>
                                        <div>
                                            {groupOption.options.map((option) => {
                                                const idsSelected = filterValue[option.group] ?? []
                                                const isChecked = idsSelected.includes(option.value)
                                                const statusColor = statusColors[option.value]
                                                const dotColor = `var(--lux-${statusColor})`
                                                return (
                                                    <div
                                                        className="flex align-items-center option-item-container"
                                                        key={option.id}>
                                                        <LuxCheckboxInput
                                                            value={isChecked}
                                                            onChangeCallback={() =>
                                                                onClickCheckbox(option)
                                                            }
                                                            id={option.id}
                                                        />
                                                        <div
                                                            className="status-dot"
                                                            style={{
                                                                backgroundColor: dotColor,
                                                            }}
                                                        />

                                                        <label
                                                            className="pl-1"
                                                            htmlFor={option.id}
                                                            onClick={() => onClickCheckbox(option)}>
                                                            {translateLabel
                                                                ? intl.formatMessage({
                                                                      id: option.label,
                                                                      defaultMessage: option.label,
                                                                  })
                                                                : option.label}
                                                        </label>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

const groupLabel = {
    task: 'activity',
    news: 'news',
    visit: 'visit',
}

export const FilterSubCategoriesProjectTypeOptions = ({
    groupOptions = {},
    filterValue = {},
    setFilterValue,
    translateLabel = false,
    onReset,
    activityTypes,
}) => {
    const intl = useIntl()

    const groups = Object.keys(groupOptions)

    const allOptions = groups.map((group) => ({
        label: intl.formatMessage({
            id: 'modals.filters.options_container.activity',
            defaultMessage: groupLabel[group],
        }),
        group,
        options: (groupOptions[group] ?? []).map((option) => ({
            value: option.value,
            label: option.label,
            id: `${group}-${option.value}`,
            group: group,
        })),
    }))

    const checkIfAllSuboptionsAreSelected = (options: any[]): boolean => {
        const selectedValues = activityTypes.reduce((acc, curr) => {
            return (
                acc +
                (filterValue[curr] || options.filter((o) => o.value.split('_')[0] === curr)).length
            )
        }, 0)
        return selectedValues === options.length
    }

    const onClickAllCheckboxes = (options: any[]) => {
        if (checkIfAllSuboptionsAreSelected(options)) {
            setFilterValue({
                ...activityTypes.reduce((acc, curr) => {
                    return {
                        ...acc,
                        [curr]: [],
                    }
                }, {}),
            })
        } else {
            setFilterValue({})
        }
    }

    const onClickReset = () => {
        onReset()
    }

    const onClickCheckbox = (type, subType) => {
        const oldValues = [...(filterValue[type] ?? [])]

        if (oldValues.includes(subType)) {
            const newValues = oldValues.filter((id) => id !== subType)
            setFilterValue({ ...filterValue, [type]: newValues })
        } else {
            const newValues = [...oldValues, subType]
            setFilterValue({ ...filterValue, [type]: newValues })
        }
    }

    const showSpinner = allOptions.some((item) => item.options?.length === 0)

    return (
        <div className="smart-modals-filter-switch-container">
            {showSpinner && (
                <div className="spinner-wrapper flex">
                    <LuxIconSpinner color={Color.GREY2} />
                </div>
            )}
            {!showSpinner && (
                <>
                    <div className="flex justify-content-end">
                        <div>
                            <OutlinedButton
                                label={'Reset'}
                                onClick={onClickReset}
                                iconClassName={'cu-reset'}
                            />
                        </div>
                    </div>
                    <div className="options-container">
                        {allOptions.map((groupOption) => (
                            <div key={groupOption.label}>
                                {groupOption.options?.length > 0 && (
                                    <>
                                        <h4 className="pb-3">{groupOption.label}</h4>
                                        <div className="select-all pb-3 align-items-center">
                                            <LuxCheckboxInput
                                                label={intl.formatMessage({
                                                    id: 'modals.filters.label.all',
                                                    defaultMessage: 'All',
                                                })}
                                                value={checkIfAllSuboptionsAreSelected(
                                                    groupOption.options
                                                )}
                                                onChangeCallback={() =>
                                                    onClickAllCheckboxes(groupOption.options)
                                                }
                                                id={`${groupOption.label}-all`}
                                            />
                                        </div>
                                        <div>
                                            {groupOption.options.map((option) => {
                                                const [type, subType] = option.value.split('_')
                                                const idsSelected = filterValue[type] || []
                                                const isChecked =
                                                    idsSelected.includes(subType) ||
                                                    !filterValue[type]
                                                return (
                                                    <div
                                                        className="flex align-items-center option-item-container"
                                                        key={option.id}>
                                                        <LuxCheckboxInput
                                                            label={
                                                                translateLabel
                                                                    ? intl.formatMessage({
                                                                          id: option.label,
                                                                          defaultMessage:
                                                                              option.label,
                                                                      })
                                                                    : option.label
                                                            }
                                                            value={isChecked}
                                                            onChangeCallback={() =>
                                                                onClickCheckbox(type, subType)
                                                            }
                                                            id={option.id}
                                                        />
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

export default FilterSubCategoryItem
